import React, { createContext, useContext, useEffect, useState } from 'react';
import { gql } from './api/client';
import { getUserSub } from './auth';
import { CONFIG } from './config';

export interface PlanningPermissions {
  can: (key: string) => boolean;
  userId: string | null;
  loading: boolean;
  error: string | null;
}

const PlanningPermissionsContext = createContext<PlanningPermissions>({
  can: () => false,
  userId: null,
  loading: true,
  error: null,
});

export function usePlanningPermissions(): PlanningPermissions {
  return useContext(PlanningPermissionsContext);
}

function useInitPermissions(): PlanningPermissions {
  const userId = getUserSub();
  // One state object, so "keys arrived" and "no longer loading" can never render separately — a
  // render between the two would briefly show a permission-denied message to a permitted user.
  const [state, setState] = useState<{ keys: string[]; loading: boolean; error: string | null }>(
    { keys: [], loading: !!userId, error: null },
  );
  const { keys, loading, error } = state;

  useEffect(() => {
    if (!userId) { setState({ keys: [], loading: false, error: null }); return; }
    gql<{ userPermissions: { permissionKey: string }[] }>(
      `query EPPerms($userId: String!, $appId: String!) {
         userPermissions(userId: $userId, applicationId: $appId) { permissionKey }
       }`,
      { userId, appId: CONFIG.applicationId }
    )
      .then(data => setState({ keys: (data.userPermissions ?? []).map(p => p.permissionKey), loading: false, error: null }))
      .catch((e: unknown) => {
        console.error('[PlanningPermissions] userPermissions query failed:', e);
        setState({ keys: [], loading: false, error: e instanceof Error ? e.message : 'Unknown error' });
      });
  }, [userId]);

  const can = (key: string): boolean => {
    if (keys.includes(key)) return true;
    if (key.endsWith(':read')) return keys.includes(key.slice(0, -5) + ':write');
    return false;
  };

  return { can, userId, loading, error };
}

export function PlanningPermissionsProvider({ children }: { children: React.ReactNode }) {
  const value = useInitPermissions();
  return React.createElement(PlanningPermissionsContext.Provider, { value }, children);
}
